import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { EzLogo, NavMobileToggle, NavDesktopLinks } from "@/pages/LandingPage";
import { CheckCircle2, Monitor, Clock, Lightbulb, ExternalLink, FileSearch, Briefcase, GraduationCap, MessageCircle, Target, Sparkles, ClipboardCheck, Quote, Star, Zap, ArrowRight, Play, Pause, Volume2, VolumeX, Mail, Laptop, Bot } from "lucide-react";
import { Button } from "@/components/ui/button";
import mixpanel from "@/mixpanel";
import { api } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function isMobileDevice() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 768;
}

const ANALYSIS_STEPS = [
  { text: "Reviewing your application...", icon: FileSearch, duration: 3000 },
  { text: "AI is analyzing your resume...", icon: Briefcase, duration: 4000 },
  { text: "Evaluating education background...", icon: GraduationCap, duration: 4000 },
  { text: "Assessing communication skills...", icon: MessageCircle, duration: 4000 },
  { text: "Matching qualifications to role...", icon: Target, duration: 4000 },
  { text: "Finalizing AI review...", icon: Sparkles, duration: 5000 },
];

const MIN_DISPLAY_TIME = 8000; // Show animation for at least 8 seconds

// Total runtime of the loader animation; scoring almost always finishes within
// this window, so we keep a steady cadence here and only back off afterward.
const ANALYSIS_DURATION = ANALYSIS_STEPS.reduce((sum, s) => sum + s.duration, 0);

// Score-status polling: poll every 3s while the loader animation is playing,
// then exponentially back off to a 15s ceiling for unusually slow scores.
const POLL_INITIAL_MS = 3000;
const POLL_MAX_MS = 15000;
const POLL_BACKOFF_FACTOR = 1.5;

const TIPS = [
  "Be warm, kind, and empathetic. We're looking for people who build trust naturally.",
  "Be yourself. Speak plainly and authentically.",
  "Stay curious. Show that you care about the details of people's lives.",
];

function AnalysisLoader({ appId, onComplete }) {
  const [progress, setProgress] = useState(0);
  const [stepIndex, setStepIndex] = useState(0);
  const startTime = useRef(Date.now());
  const pollRef = useRef(null);

  useEffect(() => { mixpanel.track("applicationReviewStarted"); }, []);

  // Animate steps
  useEffect(() => {
    let elapsed = 0;
    const timeouts = ANALYSIS_STEPS.map((step, i) => {
      elapsed += step.duration;
      return setTimeout(() => setStepIndex(i), elapsed - step.duration);
    });
    return () => timeouts.forEach(clearTimeout);
  }, []);

  // Poll for score completion, then stop (a complete result never changes).
  // Steady 3s cadence while the animation plays, backing off to 15s after.
  useEffect(() => {
    if (!appId) return;
    let cancelled = false;
    let delay = POLL_INITIAL_MS;

    const poll = async () => {
      try {
        const res = await api.get(`${API}/applications/${appId}/score-status`);
        const { status } = res.data;
        if (status === "complete" && !cancelled) {
          // Ensure minimum display time
          const elapsed = Date.now() - startTime.current;
          const remaining = Math.max(0, MIN_DISPLAY_TIME - elapsed);
          setTimeout(() => {
            if (!cancelled) {
              setProgress(100);
              setTimeout(() => onComplete(res.data), 600);
            }
          }, remaining);
          return;
        }
      } catch {}
      if (cancelled) return;
      const elapsed = Date.now() - startTime.current;
      delay = elapsed < ANALYSIS_DURATION
        ? POLL_INITIAL_MS
        : Math.min(delay * POLL_BACKOFF_FACTOR, POLL_MAX_MS);
      pollRef.current = setTimeout(poll, delay);
    };

    pollRef.current = setTimeout(poll, delay);
    return () => { cancelled = true; clearTimeout(pollRef.current); };
  }, [appId, onComplete]);

  // Smooth progress animation (reaches ~90% while waiting, 100% on complete)
  useEffect(() => {
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime.current;
      if (progress >= 100) { clearInterval(interval); return; }
      // Ease up to 90% over ~25 seconds, never quite reaching 90
      const t = Math.min(elapsed / 25000, 1);
      const eased = 90 * (1 - Math.pow(1 - t, 3));
      setProgress(Math.min(eased, 90));
    }, 100);
    return () => clearInterval(interval);
  }, [progress]);

  const StepIcon = ANALYSIS_STEPS[stepIndex]?.icon || FileSearch;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-2 sm:gap-7">
            <NavMobileToggle />
            <EzLogo size="h-7" />
            <NavDesktopLinks />
          </div>
        </div>
      </header>
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="max-w-lg w-full text-center">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-ez-blue/10 to-ez-teal/10 flex items-center justify-center mx-auto mb-6 border border-ez-blue/20">
            <ClipboardCheck className="w-10 h-10 text-ez-teal" />
          </div>
          <h2 className="text-2xl font-bold text-ez-teal font-heading mb-2">
            Reviewing Your Application
          </h2>
          <div className="flex items-center justify-center gap-2 mb-8">
            <Bot className="w-4 h-4 text-slate-400" />
            <p className="text-sm text-slate-400 font-body">Our AI is quickly reviewing your profile</p>
          </div>

          <div className="relative w-full mb-4">
            <div className="w-full bg-slate-100 rounded-full h-5 overflow-hidden shadow-inner">
              <motion.div
                className="h-full rounded-full relative overflow-hidden"
                style={{
                  width: `${Math.max(progress, 2)}%`,
                  background: "linear-gradient(90deg, #3b82f6, #0d9488, #14b8a6)",
                }}
                transition={{ duration: 0.1 }}
              >
                <div className="absolute inset-0 opacity-30" style={{
                  backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 8px, rgba(255,255,255,0.3) 8px, rgba(255,255,255,0.3) 16px)",
                  animation: "stripe-move 0.8s linear infinite",
                }} />
              </motion.div>
            </div>
            <p className="text-xs font-semibold text-ez-teal mt-2 font-heading">{Math.round(progress)}%</p>
          </div>

          <div className="flex items-center justify-center gap-3 mb-10">
            <AnimatePresence mode="wait">
              <motion.div
                key={stepIndex}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.3 }}
                className="w-8 h-8 rounded-full bg-ez-blue/10 flex items-center justify-center flex-shrink-0"
              >
                <StepIcon className="w-4 h-4 text-ez-blue" />
              </motion.div>
            </AnimatePresence>
            <AnimatePresence mode="wait">
              <motion.p
                key={stepIndex}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.3 }}
                className="text-slate-500 font-body text-sm"
              >
                {ANALYSIS_STEPS[stepIndex]?.text}
              </motion.p>
            </AnimatePresence>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.5 }}
            className="bg-ez-surface rounded-2xl border border-slate-100 p-5 text-left shadow-sm"
          >
            <Quote className="w-5 h-5 text-ez-peach/40 mb-2" />
            <p className="text-slate-600 font-body italic text-sm leading-relaxed mb-3">
              "My guide Dorothy has been such a blessing. She helped me navigate Medicare options I didn't even know existed, and she always takes the time to explain everything clearly. I feel like I finally have someone in my corner."
            </p>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                  <span className="text-emerald-700 font-bold text-xs font-heading">R</span>
                </div>
                <div>
                  <p className="font-semibold text-ez-teal text-xs font-heading">Robert P.</p>
                  <p className="text-[11px] text-slate-500 font-body">Ez Health Client</p>
                </div>
              </div>
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3.5 h-3.5 fill-ez-peach text-ez-peach" />
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      <style>{`
        @keyframes stripe-move {
          0% { background-position: 0 0; }
          100% { background-position: 32px 0; }
        }
      `}</style>
    </div>
  );
}

/* ── Video Controls ── */
function WilloVideoControls() {
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const rafRef = useRef(null);

  const getVideo = useCallback(() => document.getElementById("willo-preview-video"), []);

  const updateProgress = useCallback(() => {
    const v = getVideo();
    if (v && v.duration) setProgress((v.currentTime / v.duration) * 100);
    if (playing) rafRef.current = requestAnimationFrame(updateProgress);
  }, [playing, getVideo]);

  useEffect(() => {
    if (playing) rafRef.current = requestAnimationFrame(updateProgress);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [playing, updateProgress]);

  const toggle = () => {
    const v = getVideo();
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); } else { v.pause(); setPlaying(false); }
  };

  const toggleMute = () => {
    const v = getVideo();
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  const seek = (e) => {
    const v = getVideo();
    if (!v || !v.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    v.currentTime = pct * v.duration;
    setProgress(pct * 100);
  };

  useEffect(() => {
    const v = getVideo();
    if (!v) return;
    const onMeta = () => setDuration(v.duration);
    const onEnd = () => setPlaying(false);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    v.addEventListener("loadedmetadata", onMeta);
    v.addEventListener("ended", onEnd);
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    return () => { v.removeEventListener("loadedmetadata", onMeta); v.removeEventListener("ended", onEnd); v.removeEventListener("play", onPlay); v.removeEventListener("pause", onPause); };
  }, [getVideo]);

  const fmt = (s) => { const m = Math.floor(s / 60); return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`; };

  return (
    <>
      {!playing && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/20 pointer-events-none" data-testid="video-play-overlay">
          <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
            <Play className="w-7 h-7 text-ez-teal ml-1" />
          </div>
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-4 pb-3 pt-8 opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="h-1.5 bg-white/30 rounded-full cursor-pointer mb-3" onClick={seek}>
          <div className="h-full bg-white rounded-full relative" style={{ width: `${progress}%` }}>
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow" />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={toggle} className="text-white hover:text-white/80" data-testid="video-play-btn">
            {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>
          <button onClick={toggleMute} className="text-white hover:text-white/80" data-testid="video-mute-btn">
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
          <span className="text-white text-xs font-mono ml-auto">
            {fmt(duration * progress / 100)} / {fmt(duration)}
          </span>
        </div>
      </div>
    </>
  );
}

/* ── Check Email (mobile redirect) ── */
function CheckEmailPage() {
  useEffect(() => { mixpanel.track("willoCheckEmailViewed"); }, []);
  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="check-email-page">
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-2 sm:gap-7">
            <NavMobileToggle />
            <EzLogo size="h-7" />
            <NavDesktopLinks />
          </div>
        </div>
      </header>
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-md w-full text-center"
        >
          <div className="w-20 h-20 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-6">
            <Mail className="w-10 h-10 text-blue-500" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-ez-teal font-heading tracking-tight mb-4">
            Check your email on your computer
          </h1>
          <p className="text-slate-600 font-body text-sm leading-relaxed mb-6">
            The Willo video interview works best on a computer with a webcam. We've emailed you a link to complete the interview — open it on your laptop or desktop when you're ready.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3 text-left">
            <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800 font-body">
              <span className="font-semibold">Candidates who complete it right away are 3x more likely to get hired.</span>
            </p>
          </div>
          <div className="mt-8">
            <a href="/" className="text-xs text-slate-400 hover:text-slate-500 font-body transition-colors">Back to home</a>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

/* ── Video Card (reusable) ── */
function VideoCard() {
  return (
    <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-white">
      <div className="relative group" onClick={() => { const v = document.getElementById('willo-preview-video'); if (v) { if (v.paused) v.play(); else v.pause(); } }}>
        <video
          id="willo-preview-video"
          src="https://customer-assets.emergentagent.com/job_da635dca-2c31-4e06-bf2d-10ae4e17e29d/artifacts/c3g5ltma_fcfa0af0-e1fc-4cbb-83a1-ece291b8e768.mp4"
          className="w-full h-auto cursor-pointer"
          playsInline
          preload="metadata"
        />
        <WilloVideoControls />
      </div>
      <div className="px-4 py-3 bg-slate-50 border-t border-slate-100 flex items-center gap-2">
        <div>
          <p className="text-sm font-semibold text-ez-teal font-heading">Allison Gans</p>
          <p className="text-xs text-slate-500 font-body">Co-founder and COO of Ez Health</p>
        </div>
        <a href="https://www.linkedin.com/in/allisongans1/" target="_blank" rel="noopener noreferrer" className="ml-auto text-[#0A66C2] hover:opacity-80 transition-opacity" aria-label="Allison Gans LinkedIn">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
        </a>
      </div>
    </div>
  );
}

/* ── Main Willo Info Page ── */
function WilloInfo({ appId, willoUrl, newFlow }) {
  useEffect(() => { mixpanel.track("willoPageViewed", { new_flow: !!newFlow }); }, [newFlow]);
  const navigate = useNavigate();
  const [showCheckEmail, setShowCheckEmail] = useState(false);
  const [emailSentConfirmed, setEmailSentConfirmed] = useState(false);
  const [sending, setSending] = useState(false);

  const handleStartWillo = async (e) => {
    const mobile = isMobileDevice();
    mixpanel.track("willoInterviewStarted", { device: mobile ? "mobile" : "desktop", new_flow: !!newFlow });
    if (mobile && appId) {
      e.preventDefault();
      // Mobile: send the welcome email (new flow) or legacy move-to-willo, then show "check email" screen
      try {
        if (newFlow) {
          await api.post(`${API}/applications/${appId}/send-welcome-email`);
        } else {
          await api.post(`${API}/applications/${appId}/move-to-willo`);
        }
      } catch {}
      setShowCheckEmail(true);
      return;
    }
    // Desktop + newFlow: fire-and-forget welcome email, change copy, let link open
    if (newFlow && appId && !emailSentConfirmed && !sending) {
      setSending(true);
      try { await api.post(`${API}/applications/${appId}/send-welcome-email`); } catch {}
      setEmailSentConfirmed(true);
      setSending(false);
    }
  };

  if (showCheckEmail) return <CheckEmailPage />;

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="willo-redirect-page">
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-2 sm:gap-7">
            <NavMobileToggle />
            <EzLogo size="h-7" />
            <NavDesktopLinks />
          </div>
          <a
            href={willoUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleStartWillo}
            className="text-sm font-semibold text-ez-teal hover:text-ez-teal/80 font-heading flex items-center gap-1 transition-colors"
            data-testid="next-step-top-btn"
          >
            Next Step <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-4 py-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-xl w-full"
        >
          {/* Hero section */}
          <div className="text-center mb-6 relative">
            <div className="bg-gradient-to-b from-emerald-50 to-transparent rounded-3xl pt-6 pb-4 px-6 -mx-4">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 15 }}
                className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 rounded-full px-4 py-1.5 text-sm font-semibold font-heading mb-3"
              >
                <CheckCircle2 className="w-4 h-4" />
                <span>Application Reviewed</span>
              </motion.div>
              <h1 className="text-3xl md:text-4xl font-bold text-ez-teal font-heading tracking-tight mb-2" data-testid="willo-heading">
                You're a strong match!
              </h1>
              <div className="inline-flex items-center gap-1.5 bg-white border border-emerald-200 rounded-full px-3 py-1 text-xs font-semibold text-emerald-600 font-heading shadow-sm">
                <Zap className="w-3.5 h-3.5" /> Top 10% of applicants
              </div>
            </div>
          </div>

          {/* Bullet points */}
          <div className="text-slate-600 font-body text-sm leading-relaxed mb-6">
            <p className="font-semibold text-base text-ez-teal mb-3">
              Based on your background, we think you'd be a great fit for Ez Health!
            </p>
            <ul className="space-y-2.5">
              <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" /><span>Next: short one-way video interview via Willo. <span className="font-semibold text-ez-teal">No prep needed.</span></span></li>
              <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" /><span>Less than 10% of applicants reach this step</span></li>
              <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" /><span>Strong performance moves you to a live interview</span></li>
              <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" /><span><span className="font-semibold text-ez-teal">Everyone must pass this step</span> to move forward</span></li>
            </ul>
          </div>

          {/* Video */}
          <div className="mb-6">
            <VideoCard />
          </div>

          {/* Callouts */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-3 flex items-start gap-3">
            <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800 font-body">
              <span className="font-semibold">Candidates who complete it right away are 3x more likely to get hired.</span>
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 flex items-start gap-3">
            <Monitor className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-800 font-body">
              <span className="font-semibold">Please use a computer</span> to complete the interview for the best experience.
            </p>
          </div>

          {/* Tips */}
          <div className="bg-ez-surface border border-slate-200 rounded-xl p-5 mb-8">
            <div className="flex items-center gap-2 mb-3">
              <Lightbulb className="w-5 h-5 text-ez-peach" />
              <h3 className="font-semibold text-ez-teal text-sm font-heading">Quick Tips</h3>
            </div>
            <ul className="space-y-2 mb-3">
              {TIPS.map((tip, i) => (
                <li key={i} className="text-sm text-slate-600 font-body flex items-start gap-2">
                  <span className="text-ez-teal font-bold mt-0.5 flex-shrink-0">-</span>
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
            <a
              href="https://docs.google.com/document/d/1-s9Q2GNynKkWwVyBFd06ZEnBAc9yGK_tUZbDghZOLto/edit?tab=t.d7vbcegyvael"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-ez-blue hover:underline font-body inline-flex items-center gap-1"
            >
              Read all tips <ExternalLink className="w-3 h-3" />
            </a>
          </div>

          {/* CTA */}
          <div className="text-center mb-6">
            <Button
              data-testid="start-willo-btn"
              asChild
              className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-12 py-7 text-lg font-bold shadow-xl shadow-emerald-200 hover:shadow-emerald-300 transition-all hover:scale-[1.02] active:scale-[0.98]"
              onClick={handleStartWillo}
            >
              <a href={willoUrl} target="_blank" rel="noopener noreferrer">
                Continue to Next Step <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </Button>
            {newFlow ? (
              <p className="text-xs text-slate-500 font-body mt-3" data-testid="welcome-email-copy">
                {emailSentConfirmed
                  ? "Email sent! You can access the next step anytime."
                  : "We'll also email you the link so you can access the next step anytime."}
              </p>
            ) : (
              <p className="text-xs text-slate-400 font-body mt-3">No preparation needed. Just be yourself.</p>
            )}
          </div>

          <div className="text-center">
            <a href="/" className="text-xs text-slate-400 hover:text-slate-500 font-body transition-colors">Back to home</a>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

/* ── High-Quality Page (score >= 75) ── */
function WilloHighQuality({ appId, bookingLink: initialLink }) {
  const [bookingLink, setBookingLink] = useState(initialLink);
  const [pollExhausted, setPollExhausted] = useState(false);
  const pollRef = useRef(null);
  const startedAt = useRef(Date.now());
  const POLL_TIMEOUT_MS = 60_000; // give Ashby up to 60s to mint the link

  useEffect(() => { mixpanel.track("highQualityPageViewed", { has_link: !!initialLink }); }, [initialLink]);

  // If the booking link isn't ready yet, keep polling until it arrives or we
  // hit the timeout, in which case fall back to a "we'll email you" message.
  useEffect(() => {
    if (bookingLink || !appId) return;
    let cancelled = false;
    const poll = async () => {
      if (Date.now() - startedAt.current > POLL_TIMEOUT_MS) {
        if (!cancelled) {
          setPollExhausted(true);
          mixpanel.track("highQualityBookingLinkUnavailable", {
            wait_seconds: Math.round((Date.now() - startedAt.current) / 1000),
          });
        }
        return;
      }
      try {
        const res = await api.get(`${API}/applications/${appId}/score-status`);
        if (!cancelled && res.data?.direct_booking_link) {
          setBookingLink(res.data.direct_booking_link);
          return;
        }
      } catch {}
      if (!cancelled) pollRef.current = setTimeout(poll, 4000);
    };
    pollRef.current = setTimeout(poll, 2500);
    return () => { cancelled = true; clearTimeout(pollRef.current); };
  }, [appId, bookingLink]);

  const trackCta = (source) => {
    mixpanel.track("highQualityCtaClicked", { source, has_link: !!bookingLink });
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="willo-high-quality-page">
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-2 sm:gap-7">
            <NavMobileToggle />
            <EzLogo size="h-7" />
            <NavDesktopLinks />
          </div>
          {bookingLink ? (
            <a
              href={bookingLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackCta("top-link")}
              className="text-sm font-semibold text-ez-teal hover:text-ez-teal/80 font-heading flex items-center gap-1 transition-colors"
              data-testid="hq-next-step-top-btn"
            >
              Next step <ArrowRight className="w-4 h-4" />
            </a>
          ) : null}
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-4 py-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-xl w-full"
        >
          <div className="text-center mb-6 relative">
            <div className="bg-gradient-to-b from-emerald-50 to-transparent rounded-3xl pt-6 pb-4 px-6 -mx-4">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 15 }}
                className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 rounded-full px-4 py-1.5 text-sm font-semibold font-heading mb-3"
              >
                <CheckCircle2 className="w-4 h-4" />
                <span>Application Reviewed</span>
              </motion.div>
              <h1 className="text-3xl md:text-4xl font-bold text-ez-teal font-heading tracking-tight mb-2" data-testid="hq-heading">
                You're a top candidate!
              </h1>
              <div className="inline-flex items-center gap-1.5 bg-white border border-emerald-200 rounded-full px-3 py-1 text-xs font-semibold text-emerald-600 font-heading shadow-sm">
                <Zap className="w-3.5 h-3.5" /> Top 2% of applicants
              </div>
            </div>
          </div>

          <div className="text-slate-600 font-body text-sm leading-relaxed mb-6">
            <p className="font-semibold text-base text-ez-teal mb-3">
              Based on your background, we'd like to move quickly.
            </p>
            <ul className="space-y-2.5">
              <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" /><span>Next step: live interview with an Ez Health team member.</span></li>
              <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" /><span>Less than 2% of applicants reach this step</span></li>
              <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" /><span>Strong candidates move forward quickly in the process</span></li>
            </ul>
          </div>

          {/* CTA */}
          <div className="text-center mb-6">
            {bookingLink ? (
              <Button
                data-testid="hq-schedule-interview-btn"
                asChild
                className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-12 py-7 text-lg font-bold shadow-xl shadow-emerald-200 hover:shadow-emerald-300 transition-all hover:scale-[1.02] active:scale-[0.98]"
                onClick={() => trackCta("main")}
              >
                <a href={bookingLink} target="_blank" rel="noopener noreferrer">
                  Schedule Interview <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              </Button>
            ) : pollExhausted ? (
              <div className="inline-flex flex-col items-center gap-2" data-testid="hq-schedule-fallback">
                <div className="rounded-2xl border border-amber-200 bg-amber-50 px-6 py-5 text-left max-w-sm">
                  <p className="text-sm font-semibold text-amber-900 font-heading mb-1">We'll email you shortly</p>
                  <p className="text-xs text-amber-800 font-body leading-relaxed">
                    Your interview is being prepared. We'll send your booking link to your email within the next few minutes.
                  </p>
                </div>
              </div>
            ) : (
              <div className="inline-flex flex-col items-center gap-2" data-testid="hq-schedule-pending">
                <Button
                  disabled
                  className="rounded-full bg-emerald-600/70 text-white px-12 py-7 text-lg font-bold cursor-wait"
                >
                  <Sparkles className="w-5 h-5 mr-2 animate-pulse" />
                  Preparing your booking link…
                </Button>
                <p className="text-xs text-slate-400 font-body">This usually takes less than a minute.</p>
              </div>
            )}
          </div>

          {/* Client testimonial */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="bg-ez-surface rounded-2xl border border-slate-100 p-5 text-left shadow-sm mb-6"
            data-testid="hq-testimonial"
          >
            <Quote className="w-5 h-5 text-ez-peach/40 mb-2" />
            <p className="text-slate-600 font-body italic text-sm leading-relaxed mb-3">
              "My guide Dorothy has been such a blessing. She helped me navigate Medicare options I didn't even know existed, and she always takes the time to explain everything clearly. I feel like I finally have someone in my corner."
            </p>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                  <span className="text-emerald-700 font-bold text-xs font-heading">R</span>
                </div>
                <div>
                  <p className="font-semibold text-ez-teal text-xs font-heading">Robert P.</p>
                  <p className="text-[11px] text-slate-500 font-body">Ez Health Client</p>
                </div>
              </div>
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3.5 h-3.5 fill-ez-peach text-ez-peach" />
                ))}
              </div>
            </div>
          </motion.div>

          <div className="text-center">
            <a href="/" className="text-xs text-slate-400 hover:text-slate-500 font-body transition-colors">Back to home</a>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default function WilloRedirect() {
  const location = useLocation();
  const navigate = useNavigate();
  const [phase, setPhase] = useState("loading");
  const [willoUrl, setWilloUrl] = useState(null);
  const [bookingLink, setBookingLink] = useState(null);
  const [tier, setTier] = useState(null);
  const [newFlow, setNewFlow] = useState(false);
  const [jobType, setJobType] = useState(null);

  const appId = location.state?.appId;
  const scoring = location.state?.scoring;

  const thankYouPath = (jt) => jt === "sales" ? "/sales/thank-you" : "/thank-you";

  useEffect(() => {
    if (!appId && !scoring) {
      // Infer job type from the URL path
      const isSales = location.pathname.startsWith("/sales");
      navigate(thankYouPath(isSales ? "sales" : "guide"), { replace: true });
    }
  }, [appId, scoring, navigate, location.pathname]);

  const handleScoringComplete = useCallback((payload) => {
    const { qualified, willo_invite_url, direct_booking_link, job_type, tier: t, new_interview_flow } = payload || {};
    setJobType(job_type);
    setTier(t);
    setNewFlow(!!new_interview_flow);
    if (!qualified) {
      navigate(thankYouPath(job_type), { replace: true });
      return;
    }
    if (t === "high_quality") {
      setBookingLink(direct_booking_link || null);
      setPhase("high_quality");
    } else {
      setWilloUrl(willo_invite_url);
      setPhase("info");
    }
  }, [navigate]);

  if (phase === "loading") return <AnalysisLoader appId={appId} onComplete={handleScoringComplete} />;
  if (phase === "high_quality") return <WilloHighQuality appId={appId} bookingLink={bookingLink} />;
  return <WilloInfo appId={appId} willoUrl={willoUrl} newFlow={newFlow} />;
}
